import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Switch,
  FormControlLabel,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Grid from '@mui/material/GridLegacy';
import {
  AddOutlined as AddIcon,
  EditOutlined as EditIcon,
  DeleteOutlined as DeleteIcon,
  CalendarTodayOutlined as CalendarIcon,
  PersonOutline as PersonIcon,
  PhoneOutlined as PhoneIcon,
  EmailOutlined as EmailIcon,
  CheckCircleOutline as CheckCircleIcon,
  ErrorOutline as ErrorIcon,
  WarningAmberOutlined as WarningIcon,
  OpenInNewOutlined as OpenInNewIcon,
  ScheduleOutlined as ScheduleIcon,
  ViewListOutlined as ViewListIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  TodayOutlined as TodayIcon,
} from '@mui/icons-material';
import api from '../api';
import { asArray, availabilityWindowForDay, propertyLabel, toDate } from '../utils/showings';
import { localDateKey, toDateTimeLocalValue } from '../utils/localDateTime';
import BookingLinkCard from './BookingLinkCard';
import { readableOn, UI_CONTRAST } from '../styles/contrast';
import { isGoogleWorkspaceEnabled } from '../config/featureFlags';

// Calendar tone for each showing status; anything else renders neutral grey
const SHOWING_TONES = {
  pending: 'warning',
  scheduled: 'primary',
  completed: 'secondary',
};

// Calendar View Component for Showings
const ShowingCalendarView = ({ showings, properties, availability = [], onShowingClick, onCreateShowing }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // 'month' or 'week'
  const [showCancelled, setShowCancelled] = useState(false);
  const [dayDetail, setDayDetail] = useState(null);

  const getPropertyName = (propertyId) => {
    const property = properties.find(p => p.id === propertyId);
    return propertyLabel(property) || 'Unknown Property';
  };

  const getStatusColor = (status) => SHOWING_TONES[status] || 'default';

  const surfaces = [theme.palette.background.paper, theme.palette.background.default];
  const toneColor = (status) => {
    const tone = SHOWING_TONES[status];
    return tone ? theme.palette[tone].main : theme.palette.grey[500];
  };
  const openColor = theme.palette.success.main;
  const accentOf = (color) => readableOn(color, surfaces, UI_CONTRAST);
  const primaryAccent = accentOf(theme.palette.primary.main);

  const formatTime = (date) =>
    date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

  const shortTime = (date) => {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    return `${hours % 12 || 12}${minutes ? `:${String(minutes).padStart(2, '0')}` : ''}${hours < 12 ? 'a' : 'p'}`;
  };

  const visibleShowings = useMemo(
    () =>
      showings
        .filter((showing) => showCancelled || showing.status !== 'cancelled')
        .map((showing) => ({ ...showing, start: toDate(showing.scheduledAt) }))
        .filter((showing) => showing.start)
        .sort((a, b) => a.start - b.start),
    [showings, showCancelled]
  );

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const day = currentDate.getDate();
    
    if (viewMode === 'week') {
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(day - currentDate.getDay());
      
      const days = [];
      for (let i = 0; i < 7; i++) {
        const date = new Date(startOfWeek);
        date.setDate(startOfWeek.getDate() + i);
        days.push(date);
      }
      return days;
    } else {
      const firstDay = new Date(year, month, 1);
      const startDate = new Date(firstDay);
      startDate.setDate(startDate.getDate() - firstDay.getDay());
      
      const days = [];
      const current = new Date(startDate);
      
      for (let i = 0; i < 42; i++) {
        days.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
      return days;
    }
  }, [currentDate, viewMode]);

  // Get showings for a specific date
  const getShowingsForDate = (date) => {
    const dateStr = localDateKey(date);
    return visibleShowings.filter(showing => localDateKey(showing.start) === dateStr);
  };

  // Open availability windows on a specific date, earliest first
  const getAvailabilityForDate = (date) =>
    availability
      .map((slot) => ({ slot, window: availabilityWindowForDay(slot, date) }))
      .filter(({ window }) => window)
      .sort((a, b) => a.window.start - b.window.start);

  const availabilityTime = (window) =>
    window.allDay ? 'All day' : `${formatTime(window.start)} - ${formatTime(window.end)}`;

  const availabilityLabel = ({ slot, window }) => `${availabilityTime(window)} - ${getPropertyName(slot.propertyId)}`;

  // "4:00 – 7:00 PM" when both ends share AM/PM
  const availabilityRange = (window) => {
    if (window.allDay) return 'All day';
    const start = formatTime(window.start);
    const end = formatTime(window.end);
    const [startClock, startPeriod] = start.split(' ');
    return startPeriod === end.split(' ')[1] ? `${startClock} – ${end}` : `${start} – ${end}`;
  };

  const showingLabel = (showing) => `${formatTime(showing.start)} - ${getPropertyName(showing.propertyId)}`;

  const handlePreviousPeriod = () => {
    if (viewMode === 'week') {
      setCurrentDate(prev => {
        const newDate = new Date(prev);
        newDate.setDate(prev.getDate() - 7);
        return newDate;
      });
    } else {
      setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    }
  };

  const handleNextPeriod = () => {
    if (viewMode === 'week') {
      setCurrentDate(prev => {
        const newDate = new Date(prev);
        newDate.setDate(prev.getDate() + 7);
        return newDate;
      });
    } else {
      setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isCurrentMonth = (date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const periodTitle = (() => {
    if (viewMode !== 'week') return `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    const startOfWeek = calendarDays[0];
    const endOfWeek = calendarDays[6];
    if (startOfWeek.getMonth() === endOfWeek.getMonth()) {
      return `${startOfWeek.getDate()} - ${endOfWeek.getDate()} ${monthNames[startOfWeek.getMonth()]} ${startOfWeek.getFullYear()}`;
    }
    return `${startOfWeek.getDate()} ${monthNames[startOfWeek.getMonth()]} - ${endOfWeek.getDate()} ${monthNames[endOfWeek.getMonth()]} ${endOfWeek.getFullYear()}`;
  })();

  const periodSummary = (() => {
    const periodDays = viewMode === 'week' ? calendarDays : calendarDays.filter(isCurrentMonth);
    const periodKeys = new Set(periodDays.map(localDateKey));
    const showingCount = visibleShowings.filter((showing) => periodKeys.has(localDateKey(showing.start))).length;
    const openDayCount = periodDays.filter((day) => day >= startOfToday && getAvailabilityForDate(day).length > 0).length;
    const parts = [
      `${showingCount} ${showingCount === 1 ? 'showing' : 'showings'}`,
      `open for booking on ${openDayCount} ${openDayCount === 1 ? 'day' : 'days'}`,
    ];
    return `${parts.join(' · ')} this ${viewMode === 'week' ? 'week' : 'month'}`;
  })();

  const legend = [
    { key: 'open', label: 'Open for booking', color: openColor, dashed: true },
    { key: 'pending', label: 'Pending', color: toneColor('pending') },
    { key: 'scheduled', label: 'Scheduled', color: toneColor('scheduled') },
    { key: 'completed', label: 'Completed', color: toneColor('completed') },
    ...(showCancelled ? [{ key: 'cancelled', label: 'Cancelled', color: toneColor('cancelled'), muted: true }] : []),
  ];

  const isWeekView = viewMode === 'week';
  const stacked = isMobile && isWeekView;
  const compact = isMobile && !isWeekView;
  const wrapLabels = isWeekView;
  const availLimit = isWeekView ? 3 : isMobile ? 1 : 2;
  const showingLimit = isWeekView ? 6 : 3;
  const lastRowStart = calendarDays.length - (stacked ? 1 : 7);

  const eventChipSx = (color, { open = false, cancelled = false, clickable = false } = {}) => {
    const accent = accentOf(color);
    return {
      width: '100%',
      maxWidth: '100%',
      justifyContent: 'flex-start',
      height: wrapLabels ? 'auto' : compact ? 20 : 24,
      minHeight: compact ? 20 : 24,
      borderRadius: '8px',
      fontSize: compact ? '0.65rem' : '0.75rem',
      fontWeight: 500,
      color: cancelled ? 'text.secondary' : 'text.primary',
      bgcolor: cancelled ? 'transparent' : alpha(color, open ? 0.1 : 0.3),
      border: open ? `1px dashed ${accent}` : cancelled ? `1px solid ${theme.palette.divider}` : 'none',
      borderLeft: open ? `1px dashed ${accent}` : `3px solid ${accent}`,
      textDecoration: cancelled ? 'line-through' : 'none',
      cursor: clickable ? 'pointer' : 'inherit',
      transition: 'background-color 160ms ease, transform 160ms ease, box-shadow 160ms ease',
      '& .MuiChip-label': {
        px: compact ? 0.5 : 0.75,
        py: wrapLabels ? 0.375 : 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: wrapLabels ? 'normal' : 'nowrap',
        lineHeight: wrapLabels ? 1.35 : 'inherit',
      },
      '& .MuiChip-icon': { ml: 0.5, mr: -0.25, fontSize: 14, color: accent },
      ...(clickable && {
        '&.MuiChip-clickable:hover': {
          bgcolor: cancelled ? 'action.hover' : alpha(color, 0.45),
          transform: 'translateY(-1px)',
          boxShadow: theme.shadows[1],
        },
      }),
    };
  };

  const chipLabel = (time, place) => (wrapLabels ? (
    <>
      <Box component="span" sx={{ display: 'block', fontWeight: 600 }}>{time}</Box>
      <Box
        component="span"
        sx={{ display: 'block', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {place}
      </Box>
    </>
  ) : `${time} - ${place}`);

  const moreButtonSx = {
    alignSelf: 'flex-start',
    minWidth: 0,
    px: 0.75,
    py: 0,
    fontSize: '0.7rem',
    fontWeight: 600,
    borderRadius: '8px',
    textTransform: 'none',
  };

  return (
    <Card>
      <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
        {/* Calendar Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={isMobile ? 'stretch' : 'center'}
          flexDirection={isMobile ? 'column' : 'row'}
          gap={2}
          mb={2}
        >
          <Box>
            <Typography
              variant="h5"
              component="h2"
              sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
            >
              {periodTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {periodSummary}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.25,
                p: 0.25,
                border: 1,
                borderColor: 'divider',
                borderRadius: 999,
                bgcolor: 'background.default',
              }}
            >
              <IconButton onClick={handlePreviousPeriod} size="small" aria-label="Previous period">
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <Button
                startIcon={<TodayIcon />}
                onClick={handleToday}
                size="small"
                sx={{ borderRadius: 999, px: 1.5 }}
              >
                Today
              </Button>
              <IconButton onClick={handleNextPeriod} size="small" aria-label="Next period">
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>
            <FormControl size="small" sx={{ minWidth: 128, flex: isMobile ? 1 : 'none' }}>
              <InputLabel>View</InputLabel>
              <Select
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value)}
                label="View"
                sx={{ borderRadius: 999, bgcolor: 'background.default' }}
              >
                <MenuItem value="month">Month</MenuItem>
                <MenuItem value="week">Week</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Legend and filters */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
          mb={1.5}
        >
          <Box
            component="ul"
            aria-label="Calendar legend"
            sx={{ display: 'flex', flexWrap: 'wrap', columnGap: { xs: 1.5, sm: 2.5 }, rowGap: 0.75, m: 0, p: 0, listStyle: 'none' }}
          >
            {legend.map((item) => (
              <Box component="li" key={item.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box
                  aria-hidden
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: '4px',
                    bgcolor: item.muted ? 'transparent' : alpha(item.color, item.dashed ? 0.1 : 0.3),
                    border: item.dashed
                      ? `1px dashed ${accentOf(item.color)}`
                      : item.muted ? `1px solid ${theme.palette.divider}` : 'none',
                    borderLeft: item.dashed ? undefined : `3px solid ${accentOf(item.color)}`,
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Box>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showCancelled}
                onChange={(e) => setShowCancelled(e.target.checked)}
              />
            }
            label="Show cancelled"
            sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'text.secondary' } }}
          />
        </Box>

        {/* Calendar Grid */}
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: '16px',
            overflow: 'hidden',
            bgcolor: 'background.default',
          }}
        >
          {/* Day Headers */}
          {!stacked && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                bgcolor: alpha(theme.palette.secondary.main, 0.14),
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              {calendarDays.slice(0, 7).map((date) => (
                <Box key={date.getDay()} sx={{ py: 1, textAlign: 'center' }}>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary' }}
                  >
                    {isMobile ? dayNames[date.getDay()].charAt(0) : dayNames[date.getDay()]}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Calendar Days */}
          <Box sx={{ display: 'grid', gridTemplateColumns: stacked ? '1fr' : 'repeat(7, minmax(0, 1fr))' }}>
            {calendarDays.map((date, index) => {
              const dayShowings = getShowingsForDate(date);
              const dayAvailability = getAvailabilityForDate(date);
              const isCurrentMonthDay = isWeekView || isCurrentMonth(date);
              const isTodayDate = isToday(date);
              const isPastDay = date < startOfToday;
              const isWeekend = date.getDay() === 0 || date.getDay() === 6;
              const isEmpty = dayShowings.length === 0 && dayAvailability.length === 0;
              const openDay = (e) => {
                e.stopPropagation();
                setDayDetail(date);
              };
              const scheduleProps = isPastDay
                ? {}
                : {
                    role: 'button',
                    tabIndex: 0,
                    'aria-label': `Schedule a showing on ${date.toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric' })}`,
                    onClick: () => onCreateShowing(date),
                    onKeyDown: (e) => {
                      if (e.target !== e.currentTarget) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onCreateShowing(date);
                      }
                    },
                  };

              const dateBadge = (
                <Typography
                  variant="body2"
                  aria-current={isTodayDate ? 'date' : undefined}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: isWeekView && !isMobile ? 32 : 26,
                    height: isWeekView && !isMobile ? 32 : 26,
                    flexShrink: 0,
                    borderRadius: '50%',
                    fontSize: isWeekView && !isMobile ? '1rem' : '0.8125rem',
                    fontWeight: isTodayDate ? 700 : 500,
                    color: isTodayDate ? 'primary.contrastText' : isPastDay ? 'text.disabled' : 'text.primary',
                    bgcolor: isTodayDate ? 'primary.main' : 'transparent',
                    boxShadow: isTodayDate ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
                  }}
                >
                  {date.getDate()}
                </Typography>
              );

              const openCountChip = dayAvailability.length > 0 && !compact && (
                <Chip
                  label={`${dayAvailability.length} open`}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    borderRadius: 999,
                    color: readableOn(openColor, surfaces),
                    bgcolor: alpha(openColor, 0.16),
                    '& .MuiChip-label': { px: 0.875 },
                  }}
                />
              );

              return (
                <Box
                  key={index}
                  data-testid={`calendar-day-${localDateKey(date)}`}
                  sx={{
                    position: 'relative',
                    minWidth: 0,
                    minHeight: stacked ? 72 : isWeekView ? 240 : isMobile ? 84 : 128,
                    display: 'flex',
                    flexDirection: stacked ? 'row' : 'column',
                    alignItems: stacked ? 'flex-start' : 'stretch',
                    gap: stacked ? 1.5 : 0.5,
                    p: stacked ? 1.25 : { xs: 0.5, sm: 1 },
                    boxSizing: 'border-box',
                    opacity: isCurrentMonthDay ? 1 : 0.45,
                    bgcolor: isTodayDate
                      ? alpha(theme.palette.primary.main, 0.1)
                      : isPastDay
                        ? alpha(theme.palette.text.primary, 0.025)
                        : isWeekend
                          ? alpha(theme.palette.secondary.main, 0.08)
                          : 'transparent',
                    boxShadow: isTodayDate ? `inset 0 3px 0 ${primaryAccent}` : 'none',
                    cursor: isPastDay ? 'default' : 'pointer',
                    transition: 'background-color 160ms ease',
                    '& .calendar-day-add': { opacity: 0, transition: 'opacity 160ms ease' },
                    '&:hover': isPastDay ? {} : {
                      bgcolor: alpha(theme.palette.primary.main, isTodayDate ? 0.16 : 0.07),
                      '& .calendar-day-add': { opacity: 1 },
                    },
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: primaryAccent,
                      outlineOffset: -2,
                    },
                    borderRight: !stacked && index % 7 !== 6 ? 1 : 0,
                    borderBottom: index < lastRowStart ? 1 : 0,
                    borderColor: 'divider',
                  }}
                  {...scheduleProps}
                >
                  {stacked ? (
                    <Box sx={{ width: 44, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary', lineHeight: 1.4 }}
                      >
                        {dayNames[date.getDay()]}
                      </Typography>
                      {dateBadge}
                    </Box>
                  ) : (
                    <Box display="flex" alignItems="center" gap={0.5} mb={0.25} minWidth={0}>
                      {dateBadge}
                      <Box flex={1} />
                      {!isPastDay && !isMobile && (
                        <AddIcon className="calendar-day-add" aria-hidden sx={{ fontSize: 18, color: primaryAccent }} />
                      )}
                      {openCountChip}
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, minWidth: 0, width: stacked ? 'auto' : '100%' }}>
                    {stacked && openCountChip && <Box>{openCountChip}</Box>}

                    {/* Availability for this day */}
                    {dayAvailability.slice(0, availLimit).map((entry) => (
                      <Chip
                        key={entry.slot.id}
                        icon={compact ? undefined : <ScheduleIcon />}
                        label={compact
                          ? (entry.window.allDay ? 'All day' : shortTime(entry.window.start))
                          : wrapLabels
                            ? chipLabel(availabilityRange(entry.window), getPropertyName(entry.slot.propertyId))
                            : availabilityLabel(entry)
                        }
                        title={availabilityLabel(entry)}
                        size="small"
                        sx={eventChipSx(openColor, { open: true })}
                      />
                    ))}
                    {dayAvailability.length > availLimit && (
                      <Button size="small" onClick={openDay} sx={moreButtonSx}>
                        +{dayAvailability.length - availLimit} more open
                      </Button>
                    )}

                    {/* Showings for this day */}
                    {dayShowings.slice(0, showingLimit).map((showing) => (
                      <Chip
                        key={showing.id}
                        label={compact
                          ? shortTime(showing.start)
                          : chipLabel(formatTime(showing.start), getPropertyName(showing.propertyId))
                        }
                        title={`${showingLabel(showing)} (${showing.status})`}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onShowingClick(showing);
                        }}
                        sx={eventChipSx(toneColor(showing.status), {
                          cancelled: showing.status === 'cancelled',
                          clickable: true,
                        })}
                      />
                    ))}
                    {dayShowings.length > showingLimit && (
                      <Button size="small" onClick={openDay} sx={moreButtonSx}>
                        +{dayShowings.length - showingLimit} more
                      </Button>
                    )}

                    {stacked && isEmpty && (
                      <Typography variant="body2" color="text.disabled" sx={{ py: 0.5 }}>
                        {isPastDay ? 'Nothing scheduled' : 'Nothing scheduled yet'}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </CardContent>

      <Dialog open={Boolean(dayDetail)} onClose={() => setDayDetail(null)} maxWidth="xs" fullWidth>
        {dayDetail && (() => {
          const detailShowings = getShowingsForDate(dayDetail);
          const detailAvailability = getAvailabilityForDate(dayDetail);
          return (
            <>
              <DialogTitle>
                {dayDetail.toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric' })}
              </DialogTitle>
              <DialogContent dividers>
                <Typography variant="subtitle2" gutterBottom>
                  Showings ({detailShowings.length})
                </Typography>
                {detailShowings.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    No showings this day.
                  </Typography>
                ) : (
                  <List dense disablePadding sx={{ mb: 2 }}>
                    {detailShowings.map((showing) => (
                      <ListItemButton
                        key={showing.id}
                        onClick={() => {
                          setDayDetail(null);
                          onShowingClick(showing);
                        }}
                      >
                        <ListItemText
                          primary={showingLabel(showing)}
                          secondary={showing.contactName || null}
                          sx={{ textDecoration: showing.status === 'cancelled' ? 'line-through' : 'none' }}
                        />
                        <Chip
                          label={showing.status}
                          size="small"
                          color={getStatusColor(showing.status)}
                          variant={showing.status === 'cancelled' ? 'outlined' : 'filled'}
                          sx={{ ml: 1, textTransform: 'capitalize' }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                )}
                <Typography variant="subtitle2" gutterBottom>
                  Open for booking ({detailAvailability.length})
                </Typography>
                {detailAvailability.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No availability this day.
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {detailAvailability.map((entry) => (
                      <ListItemText key={entry.slot.id} primary={availabilityLabel(entry)} sx={{ px: 2, py: 0.5 }} />
                    ))}
                  </List>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDayDetail(null)}>Close</Button>
                {dayDetail >= startOfToday && (
                  <Button
                    variant="contained"
                    onClick={() => {
                      const day = dayDetail;
                      setDayDetail(null);
                      onCreateShowing(day);
                    }}
                  >
                    Schedule a showing
                  </Button>
                )}
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
    </Card>
  );
};

const ShowingScheduler = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const googleCalendarEnabled = isGoogleWorkspaceEnabled();
  
  const [showings, setShowings] = useState([]);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingShowing, setEditingShowing] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [availability, setAvailability] = useState([]);
  const [availabilityDialogOpen, setAvailabilityDialogOpen] = useState(false);
  const [availabilityFormData, setAvailabilityFormData] = useState({
    propertyId: '',
    unitId: '',
    startTime: '',
    endTime: '',
    duration: 60,
    recurring: false,
  });
  const [saving, setSaving] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [landlordBookingUrl, setLandlordBookingUrl] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    propertyId: '',
    unitId: '',
    scheduledAt: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    notes: '',
    status: 'scheduled',
  });

  useEffect(() => {
    fetchShowings();
    fetchProperties();
    fetchAvailability();
    fetchBookingLink();
  }, []);

  const fetchBookingLink = async () => {
    try {
      const response = await api.get('/api/showings/booking-link');
      if (response.data?.url) {
        setLandlordBookingUrl(response.data.url);
      }
    } catch (err) {
      console.error('Error fetching booking link:', err);
    }
  };

  const fetchAvailability = async () => {
    try {
      const response = await api.get('/api/availability');
      setAvailability(asArray(response.data));
    } catch (err) {
      console.error('Error fetching availability:', err);
    }
  };

  const fetchShowings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/showings');
      setShowings(asArray(response.data));
      setError(null);
    } catch (err) {
      console.error('Error fetching showings:', err);
      setError(err.response?.data?.error || 'Failed to load showings');
    } finally {
      setLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await api.get('/api/properties');
      setProperties(asArray(response.data));
    } catch (err) {
      console.error('Error fetching properties:', err);
    }
  };

  const fetchUnitsForProperty = (propertyId) => {
    if (!propertyId) {
      setUnits([]);
      return;
    }
    
    // Find the property and get its units array
    const property = properties.find(p => p.id === propertyId);
    if (property && property.units) {
      setUnits(property.units);
    } else {
      setUnits([]);
    }
  };

  const handlePropertyChange = (propertyId) => {
    setFormData({ ...formData, propertyId, unitId: '' });
    fetchUnitsForProperty(propertyId);
  };

  const handleOpenDialog = (showing = null, day = null) => {
    if (showing) {
      // Editing existing showing
      setEditingShowing(showing);
      setFormData({
        propertyId: showing.propertyId || '',
        unitId: showing.unitId || '',
        scheduledAt: toDateTimeLocalValue(showing.scheduledAt),
        contactName: showing.contactName || '',
        contactEmail: showing.contactEmail || '',
        contactPhone: showing.contactPhone || '',
        notes: showing.notes || '',
        status: showing.status || 'scheduled',
      });
      fetchUnitsForProperty(showing.propertyId);
    } else {
      // Creating new showing
      setEditingShowing(null);
      setFormData({
        propertyId: '',
        unitId: '',
        scheduledAt: day ? `${localDateKey(day)}T10:00` : '',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        notes: '',
        status: 'scheduled',
      });
      setUnits([]);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingShowing(null);
    setFormData({
      propertyId: '',
      unitId: '',
      scheduledAt: '',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      notes: '',
      status: 'scheduled',
    });
    setUnits([]);
  };

  const handleSubmit = async () => {
    try {
      // Validation
      if (!formData.propertyId || !formData.scheduledAt || !formData.contactName) {
        setError('Please fill in property, date/time, and contact name');
        return;
      }

      setSaving(true);
      setError(null);

      const payload = {
        ...formData,
        scheduledAt: new Date(formData.scheduledAt).toISOString(),
        unitId: formData.unitId || null,
        contactEmail: formData.contactEmail || null,
        contactPhone: formData.contactPhone || null,
      };

      if (editingShowing) {
        await api.put(`/api/showings/${editingShowing.id}`, payload);
        setSuccess('Showing updated');
      } else {
        const res = await api.post('/api/showings', payload);
        const confirmed = res.data?.confirmationSent
          ? ' Confirmation email/SMS attempted.'
          : '';
        setSuccess(`Showing scheduled.${confirmed}`);
      }

      handleCloseDialog();
      await fetchShowings();
    } catch (err) {
      console.error('Error saving showing:', err);
      setError(err.response?.data?.error || err.response?.data?.details || 'Failed to save showing');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (showingId) => {
    if (!window.confirm('Are you sure you want to delete this showing?')) {
      return;
    }

    try {
      await api.delete(`/api/showings/${showingId}`);
      setSuccess('Showing deleted');
      await fetchShowings();
    } catch (err) {
      console.error('Error deleting showing:', err);
      setError(err.response?.data?.error || 'Failed to delete showing');
    }
  };

  const handleConfirmPending = async (showing) => {
    if (!showing?.id) return;
    try {
      setStatusUpdatingId(showing.id);
      setError(null);
      await api.post(`/api/showings/${showing.id}/confirm`);
      setSuccess('Showing confirmed — prospect will be notified.');
      await fetchShowings();
    } catch (err) {
      console.error('Error confirming showing:', err);
      setError(err.response?.data?.error || 'Failed to confirm showing');
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleStatusUpdate = async (showing, status) => {
    if (!showing?.id) return;
    try {
      setStatusUpdatingId(showing.id);
      setError(null);
      await api.put(`/api/showings/${showing.id}`, { status });
      setSuccess(
        status === 'completed'
          ? 'Showing marked completed (confirmed)'
          : status === 'cancelled'
            ? 'Showing cancelled'
            : `Showing marked ${status}`
      );
      await fetchShowings();
    } catch (err) {
      console.error('Error updating showing status:', err);
      setError(err.response?.data?.error || 'Failed to update showing status');
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const getPropertyName = (propertyId) => {
    const property = properties.find(p => p.id === propertyId);
    return propertyLabel(property) || 'Unknown Property';
  };

  const getUnitName = (propertyId, unitId) => {
    if (!unitId) return 'General Property';
    const property = properties.find(p => p.id === propertyId);
    if (!property || !property.units) return `Unit ${unitId}`;
    
    const unit = property.units.find(u => u.id === unitId);
    if (!unit) return `Unit ${unitId}`;
    
    // Return unit name, unit number, or fallback to ID
    return unit.unitNumber || unit.name || `Unit ${unit.id}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled':
        return 'primary';
      case 'pending':
        return 'warning';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getSyncStatus = (showing) => {
    if (showing.calendarSyncError) return 'error';
    if (showing.calendarEventId) return 'synced';
    return 'pending';
  };

  const getSyncStatusIcon = (syncStatus) => {
    switch (syncStatus) {
      case 'synced':
        return <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />;
      case 'error':
        return <ErrorIcon fontSize="small" sx={{ color: 'error.main' }} />;
      case 'pending':
        return <WarningIcon fontSize="small" sx={{ color: 'warning.main' }} />;
      default:
        return null;
    }
  };

  const getSyncStatusText = (syncStatus) => {
    switch (syncStatus) {
      case 'synced':
        return 'Synced';
      case 'error':
        return 'Sync Error';
      case 'pending':
        return 'Pending';
      default:
        return '';
    }
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return '';
    const date = new Date(dateTime);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Group showings by upcoming vs past
  const now = new Date();
  const upcomingShowings = showings.filter(s =>
    new Date(s.scheduledAt) >= now && ['scheduled', 'pending'].includes(s.status)
  ).sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));

  const pastShowings = showings.filter(s =>
    !(new Date(s.scheduledAt) >= now && ['scheduled', 'pending'].includes(s.status))
  ).sort((a, b) => new Date(b.scheduledAt) - new Date(a.scheduledAt));

  // Filter availability slots - only show active (not expired) and remove duplicates
  const activeAvailability = availability
    .filter((slot, index, self) => {
      // Remove duplicates by checking if this is the first occurrence of this ID
      const firstIndex = self.findIndex(s => s.id === slot.id);
      if (index !== firstIndex) return false;
      
      // Filter out expired slots (endTime is in the past)
      const slotEnd = toDate(slot.endTime) || new Date(slot.endTime);
      return slotEnd >= now;
    })
    .sort((a, b) => {
      const aStart = toDate(a.startTime) || new Date(a.startTime);
      const bStart = toDate(b.startTime) || new Date(b.startTime);
      return aStart - bStart;
    });
  
  const expiredAvailability = availability
    .filter((slot, index, self) => {
      // Remove duplicates
      const firstIndex = self.findIndex(s => s.id === slot.id);
      if (index !== firstIndex) return false;
      
      // Only include expired slots
      const slotEnd = toDate(slot.endTime) || new Date(slot.endTime);
      return slotEnd < now;
    })
    .sort((a, b) => {
      const aEnd = toDate(a.endTime) || new Date(a.endTime);
      const bEnd = toDate(b.endTime) || new Date(b.endTime);
      return bEnd - aEnd; // Most recently expired first
    });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' },
        mb: { xs: 2, sm: 3 },
        gap: { xs: 2, sm: 0 }
      }}>
        <Typography 
          variant="h4" 
          component="h1"
          sx={{ 
            fontSize: { xs: '1.5rem', sm: '2rem' },
            mb: { xs: 1, sm: 0 }
          }}
        >
          Showing Scheduler
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          gap: { xs: 1, sm: 2 }, 
          flexDirection: { xs: 'column', sm: 'row' }, 
          width: { xs: '100%', sm: 'auto' },
          flexWrap: 'wrap'
        }}>
          <Button
            variant="outlined"
            startIcon={<ScheduleIcon />}
            onClick={() => setAvailabilityDialogOpen(true)}
            fullWidth={isMobile}
            size={isMobile ? 'small' : 'medium'}
          >
            {isMobile ? 'Availability' : 'Manage Availability'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            fullWidth={isMobile}
            size={isMobile ? 'small' : 'medium'}
          >
            {isMobile ? 'Schedule' : 'Schedule Showing'}
          </Button>
        </Box>
      </Box>

      {/* View Mode Tabs */}
      <Paper sx={{ mb: { xs: 2, sm: 3 } }}>
        <Tabs
          value={viewMode}
          onChange={(e, newValue) => setViewMode(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<ViewListIcon />} label="List View" value="list" />
          <Tab icon={<CalendarIcon />} label="Calendar View" value="calendar" />
        </Tabs>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: { xs: 2, sm: 3 } }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: { xs: 2, sm: 3 } }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <BookingLinkCard
        url={landlordBookingUrl}
        properties={properties}
        propertyIdsWithAvailability={new Set(activeAvailability.map((slot) => slot.propertyId))}
        onMessage={(message) => {
          setError(null);
          setSuccess(message);
        }}
        onError={(message) => {
          setSuccess(null);
          setError(message);
        }}
      />

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <Box sx={{ mb: { xs: 2, sm: 4 } }}>
          <ShowingCalendarView 
            showings={showings}
            properties={properties}
            availability={availability}
            onShowingClick={(showing) => handleOpenDialog(showing)}
            onCreateShowing={(day) => handleOpenDialog(null, day)}
          />
        </Box>
      )}

      {/* Availability Slots */}
      {viewMode === 'list' && (
      <Box sx={{ mb: { xs: 2, sm: 4 } }}>
        <Typography 
          variant="h6" 
          sx={{ 
            mb: { xs: 1.5, sm: 2 }, 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            fontSize: { xs: '1.1rem', sm: '1.25rem' }
          }}
        >
          <ScheduleIcon /> Active Availability Slots ({activeAvailability.length})
        </Typography>
        
        {activeAvailability.length === 0 ? (
          <Alert severity="info">No active availability slots. Click "Manage Availability" to create slots.</Alert>
        ) : (
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            {activeAvailability.map((slot) => {
              const slotStart = slot.startTime?.toDate ? slot.startTime.toDate() : new Date(slot.startTime);
              const slotEnd = slot.endTime?.toDate ? slot.endTime.toDate() : new Date(slot.endTime);
              const propertyName = getPropertyName(slot.propertyId);
              const unitName = getUnitName(slot.propertyId, slot.unitId);
              
              return (
                <Grid item xs={12} md={6} key={slot.id}>
                  <Card>
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <Typography variant="h6" component="div">
                            {propertyName}
                          </Typography>
                          {unitName && (
                            <Typography variant="body2" color="text.secondary">
                              {unitName}
                            </Typography>
                          )}
                        </Box>
                        <Chip
                          label="Available"
                          color="success"
                          size="small"
                          variant="outlined"
                        />
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 0.75, sm: 1 } }}>
                        <CalendarIcon fontSize="small" color="action" />
                        <Typography 
                          variant="body2"
                          sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                        >
                          {formatDateTime(slotStart)} - {formatDateTime(slotEnd)}
                        </Typography>
                      </Box>

                      {slot.duration && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 0.75, sm: 1 } }}>
                          <ScheduleIcon fontSize="small" color="action" />
                          <Typography 
                            variant="body2"
                            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                          >
                            Duration: {slot.duration} minutes
                          </Typography>
                        </Box>
                      )}

                      {slot.recurring && (
                        <Chip
                          label="Recurring"
                          size="small"
                          color="info"
                          variant="outlined"
                          sx={{ mt: 1 }}
                        />
                      )}

                      <Box sx={{ 
                        display: 'flex', 
                        gap: { xs: 1, sm: 1 }, 
                        mt: { xs: 1.5, sm: 2 },
                        justifyContent: { xs: 'flex-start', sm: 'flex-start' }
                      }}>
                        <IconButton
                          size={isMobile ? 'small' : 'medium'}
                          color="error"
                          onClick={async () => {
                            if (window.confirm('Delete this availability slot?')) {
                              try {
                                await api.delete(`/api/availability/${slot.id}`);
                                setSuccess('Availability slot deleted successfully');
                                fetchAvailability();
                              } catch (err) {
                                setError('Failed to delete availability slot');
                              }
                            }
                          }}
                          aria-label="Delete availability slot"
                        >
                          <DeleteIcon fontSize={isMobile ? 'small' : 'medium'} />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
      )}

      {/* Expired Availability Slots (Optional) */}
      {viewMode === 'list' && expiredAvailability.length > 0 && (
      <Box sx={{ mb: { xs: 2, sm: 4 } }}>
        <Typography 
          variant="h6" 
          sx={{ 
            mb: { xs: 1.5, sm: 2 }, 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            fontSize: { xs: '1.1rem', sm: '1.25rem' },
            color: 'text.secondary'
          }}
        >
          <ScheduleIcon /> Expired Availability Slots ({expiredAvailability.length})
        </Typography>
        
        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
          {expiredAvailability.map((slot) => {
            const slotStart = slot.startTime?.toDate ? slot.startTime.toDate() : new Date(slot.startTime);
            const slotEnd = slot.endTime?.toDate ? slot.endTime.toDate() : new Date(slot.endTime);
            const propertyName = getPropertyName(slot.propertyId);
            const unitName = getUnitName(slot.propertyId, slot.unitId);
            
            return (
              <Grid item xs={12} md={6} key={slot.id}>
                <Card sx={{ opacity: 0.6 }}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" component="div">
                          {propertyName}
                        </Typography>
                        {unitName && (
                          <Typography variant="body2" color="text.secondary">
                            {unitName}
                          </Typography>
                        )}
                      </Box>
                      <Chip
                        label="Expired"
                        color="default"
                        size="small"
                        variant="outlined"
                      />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 0.75, sm: 1 } }}>
                      <CalendarIcon fontSize="small" color="action" />
                      <Typography 
                        variant="body2"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        {formatDateTime(slotStart)} - {formatDateTime(slotEnd)}
                      </Typography>
                    </Box>

                    <Box sx={{ 
                      display: 'flex', 
                      gap: { xs: 1, sm: 1 }, 
                      mt: { xs: 1.5, sm: 2 },
                      justifyContent: { xs: 'flex-start', sm: 'flex-start' }
                    }}>
                      <IconButton
                        size={isMobile ? 'small' : 'medium'}
                        color="error"
                        onClick={async () => {
                          if (window.confirm('Delete this expired availability slot?')) {
                            try {
                              await api.delete(`/api/availability/${slot.id}`);
                              setSuccess('Availability slot deleted successfully');
                              fetchAvailability();
                            } catch (err) {
                              setError('Failed to delete availability slot');
                            }
                          }
                        }}
                        aria-label="Delete expired availability slot"
                      >
                        <DeleteIcon fontSize={isMobile ? 'small' : 'medium'} />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
      )}

      {/* Upcoming Showings */}
      {viewMode === 'list' && (
      <Box sx={{ mb: { xs: 2, sm: 4 } }}>
        <Typography 
          variant="h6" 
          sx={{ 
            mb: { xs: 1.5, sm: 2 }, 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            fontSize: { xs: '1.1rem', sm: '1.25rem' }
          }}
        >
          <CalendarIcon /> Upcoming Showings ({upcomingShowings.length})
        </Typography>
        
        {upcomingShowings.length === 0 ? (
          <Alert severity="info">No upcoming showings scheduled</Alert>
        ) : (
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            {upcomingShowings.map((showing) => (
              <Grid item xs={12} md={6} key={showing.id}>
                <Card>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" component="div">
                          {getPropertyName(showing.propertyId)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {getUnitName(showing.propertyId, showing.unitId)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                        <Chip
                          label={showing.status}
                          color={getStatusColor(showing.status)}
                          size="small"
                        />
                        {googleCalendarEnabled && (() => {
                          const syncStatus = getSyncStatus(showing);
                          return (
                            <Chip
                              icon={getSyncStatusIcon(syncStatus)}
                              label={getSyncStatusText(syncStatus)}
                              size="small"
                              color={syncStatus === 'synced' ? 'success' : syncStatus === 'error' ? 'error' : 'warning'}
                              variant="outlined"
                            />
                          );
                        })()}
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 0.75, sm: 1 } }}>
                      <CalendarIcon fontSize="small" color="action" />
                      <Typography 
                        variant="body2"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        {formatDateTime(showing.scheduledAt)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 0.75, sm: 1 } }}>
                      <PersonIcon fontSize="small" color="action" />
                      <Typography 
                        variant="body2"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        {showing.contactName}
                      </Typography>
                    </Box>

                    {showing.contactEmail && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 0.75, sm: 1 } }}>
                        <EmailIcon fontSize="small" color="action" />
                        <Typography 
                          variant="body2"
                          sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                        >
                          {showing.contactEmail}
                        </Typography>
                      </Box>
                    )}

                    {showing.contactPhone && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 0.75, sm: 1 } }}>
                        <PhoneIcon fontSize="small" color="action" />
                        <Typography 
                          variant="body2"
                          sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                        >
                          {showing.contactPhone}
                        </Typography>
                      </Box>
                    )}

                    {showing.notes && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                        {showing.notes}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                      {showing.bookedBy === 'prospect' && (
                        <Chip label="Public booking" size="small" variant="outlined" />
                      )}
                      {showing.confirmationSent && (
                        <Chip label="Confirmation sent" size="small" color="success" variant="outlined" />
                      )}
                    </Box>

                    {googleCalendarEnabled && showing.calendarSyncError && (
                      <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                        <Typography variant="caption" component="div">
                          <strong>Calendar Sync Error:</strong> {showing.calendarSyncError}
                        </Typography>
                      </Alert>
                    )}

                    {googleCalendarEnabled && showing.calendarEventUrl && (
                      <Box sx={{ mt: 1, mb: 1 }}>
                        <Button
                          size={isMobile ? 'small' : 'medium'}
                          variant="outlined"
                          startIcon={<OpenInNewIcon />}
                          onClick={() => {
                            window.open(showing.calendarEventUrl, '_blank', 'noopener,noreferrer');
                          }}
                          fullWidth={isMobile}
                        >
                          {isMobile ? 'Open Calendar' : 'Open in Google Calendar'}
                        </Button>
                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                          sx={{ 
                            display: 'block',
                            mt: 0.5,
                            fontSize: '0.7rem',
                            fontStyle: 'italic',
                            lineHeight: 1.3
                          }}
                          title="If multiple Google accounts are logged in, the link may open in the wrong account."
                        >
                          Tip: If the wrong Google account opens, switch manually in Calendar
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ 
                      display: 'flex', 
                      gap: 1, 
                      mt: { xs: 1.5, sm: 2 },
                      flexWrap: 'wrap',
                      alignItems: 'center'
                    }}>
                      {showing.status === 'pending' && (
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          disabled={statusUpdatingId === showing.id}
                          onClick={() => handleConfirmPending(showing)}
                        >
                          {statusUpdatingId === showing.id ? '…' : 'Confirm showing'}
                        </Button>
                      )}
                      {showing.status === 'scheduled' && (
                        <>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            disabled={statusUpdatingId === showing.id}
                            onClick={() => handleStatusUpdate(showing, 'completed')}
                          >
                            {statusUpdatingId === showing.id ? '…' : 'Confirm complete'}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={statusUpdatingId === showing.id}
                            onClick={() => handleStatusUpdate(showing, 'cancelled')}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="small"
                            variant="text"
                            disabled={statusUpdatingId === showing.id}
                            onClick={() => handleStatusUpdate(showing, 'no-show')}
                          >
                            No-show
                          </Button>
                        </>
                      )}
                      <IconButton
                        size={isMobile ? 'small' : 'medium'}
                        color="primary"
                        onClick={() => handleOpenDialog(showing)}
                        aria-label="Edit showing"
                      >
                        <EditIcon fontSize={isMobile ? 'small' : 'medium'} />
                      </IconButton>
                      <IconButton
                        size={isMobile ? 'small' : 'medium'}
                        color="error"
                        onClick={() => handleDelete(showing.id)}
                        aria-label="Delete showing"
                      >
                        <DeleteIcon fontSize={isMobile ? 'small' : 'medium'} />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
      )}

      {/* Past Showings */}
      {viewMode === 'list' && pastShowings.length > 0 && (
        <Box>
          <Typography 
            variant="h6" 
            sx={{ 
              mb: { xs: 1.5, sm: 2 },
              fontSize: { xs: '1.1rem', sm: '1.25rem' }
            }}
          >
            Past Showings ({pastShowings.length})
          </Typography>
          
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            {pastShowings.map((showing) => (
              <Grid item xs={12} md={6} key={showing.id}>
                <Card sx={{ opacity: 0.7 }}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" component="div">
                          {getPropertyName(showing.propertyId)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {getUnitName(showing.propertyId, showing.unitId)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                        <Chip
                          label={showing.status}
                          color={getStatusColor(showing.status)}
                          size="small"
                        />
                        {googleCalendarEnabled && (() => {
                          const syncStatus = getSyncStatus(showing);
                          return (
                            <Chip
                              icon={getSyncStatusIcon(syncStatus)}
                              label={getSyncStatusText(syncStatus)}
                              size="small"
                              color={syncStatus === 'synced' ? 'success' : syncStatus === 'error' ? 'error' : 'warning'}
                              variant="outlined"
                            />
                          );
                        })()}
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <CalendarIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {formatDateTime(showing.scheduledAt)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {showing.contactName}
                      </Typography>
                    </Box>

                    {googleCalendarEnabled && showing.calendarSyncError && (
                      <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                        <Typography variant="caption" component="div">
                          <strong>Calendar Sync Error:</strong> {showing.calendarSyncError}
                        </Typography>
                      </Alert>
                    )}

                    {googleCalendarEnabled && showing.calendarEventUrl && (
                      <Box sx={{ mt: 1, mb: 1 }}>
                        <Button
                          size={isMobile ? 'small' : 'medium'}
                          variant="outlined"
                          startIcon={<OpenInNewIcon />}
                          onClick={() => {
                            window.open(showing.calendarEventUrl, '_blank', 'noopener,noreferrer');
                          }}
                          fullWidth={isMobile}
                        >
                          {isMobile ? 'Open Calendar' : 'Open in Google Calendar'}
                        </Button>
                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                          sx={{ 
                            display: 'block',
                            mt: 0.5,
                            fontSize: '0.7rem',
                            fontStyle: 'italic',
                            lineHeight: 1.3
                          }}
                          title="If multiple Google accounts are logged in, the link may open in the wrong account. The event is correctly synced to your connected account - manually switch accounts in Google Calendar if needed."
                        >
                          💡 Tip: If wrong account opens, switch manually
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Availability Management Dialog */}
      <Dialog 
        open={availabilityDialogOpen} 
        onClose={() => setAvailabilityDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Manage Availability
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 }, mt: { xs: 1, sm: 2 } }}>
            <FormControl fullWidth required>
              <InputLabel>Property</InputLabel>
              <Select
                value={availabilityFormData.propertyId}
                onChange={(e) => {
                  setAvailabilityFormData({ ...availabilityFormData, propertyId: e.target.value, unitId: '' });
                  fetchUnitsForProperty(e.target.value);
                }}
                label="Property"
              >
                {properties.map((property) => (
                  <MenuItem key={property.id} value={property.id}>
                    {propertyLabel(property) || 'Property'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Unit (Optional)</InputLabel>
              <Select
                value={availabilityFormData.unitId}
                onChange={(e) => setAvailabilityFormData({ ...availabilityFormData, unitId: e.target.value })}
                label="Unit (Optional)"
                disabled={!availabilityFormData.propertyId}
              >
                <MenuItem value="">General Property</MenuItem>
                {units.map((unit) => (
                  <MenuItem key={unit.id} value={unit.id}>
                    {unit.unitNumber || unit.name || `Unit ${unit.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Start Time"
              type="datetime-local"
              value={availabilityFormData.startTime}
              onChange={(e) => setAvailabilityFormData({ ...availabilityFormData, startTime: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required
              fullWidth
            />

            <TextField
              label="End Time"
              type="datetime-local"
              value={availabilityFormData.endTime}
              onChange={(e) => setAvailabilityFormData({ ...availabilityFormData, endTime: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required
              fullWidth
            />

            <TextField
              label="Slot Duration (minutes)"
              type="number"
              value={availabilityFormData.duration}
              onChange={(e) => setAvailabilityFormData({ ...availabilityFormData, duration: parseInt(e.target.value) || 60 })}
              fullWidth
            />

            <Alert severity="info">
              Set your available times for property showings. These will be visible to prospects when they book showings.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => setAvailabilityDialogOpen(false)}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
            onClick={async () => {
              const { propertyId, startTime, endTime } = availabilityFormData;
              if (!propertyId || !startTime || !endTime) {
                setError('Please choose a property, start time, and end time');
                return;
              }
              try {
                await api.post('/api/availability', {
                  ...availabilityFormData,
                  startTime: new Date(startTime).toISOString(),
                  endTime: new Date(endTime).toISOString(),
                });
                setAvailabilityDialogOpen(false);
                setSuccess('Availability slot created successfully!');
                setError(null);
                fetchAvailability();
                setAvailabilityFormData({
                  propertyId: '',
                  unitId: '',
                  startTime: '',
                  endTime: '',
                  duration: 60,
                  recurring: false,
                });
                // Clear success message after 5 seconds
                setTimeout(() => setSuccess(null), 5000);
              } catch (err) {
                console.error('Error creating availability:', err);
                const errorMessage = err.response?.data?.details || err.response?.data?.error || err.message || 'Failed to create availability slot';
                setError(`Failed to create availability slot: ${errorMessage}`);
                setSuccess(null);
              }
            }}
          >
            Save Availability
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          {editingShowing ? 'Edit Showing' : 'Schedule New Showing'}
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 }, mt: { xs: 1, sm: 2 } }}>
            <FormControl fullWidth required>
              <InputLabel>Property</InputLabel>
              <Select
                value={formData.propertyId}
                onChange={(e) => handlePropertyChange(e.target.value)}
                label="Property"
              >
                {properties.map((property) => (
                  <MenuItem key={property.id} value={property.id}>
                    {propertyLabel(property) || 'Property'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Unit (Optional)</InputLabel>
              <Select
                value={formData.unitId}
                onChange={(e) => setFormData({ ...formData, unitId: e.target.value })}
                label="Unit (Optional)"
                disabled={!formData.propertyId}
              >
                <MenuItem value="">
                  <em>General Property Showing</em>
                </MenuItem>
                {units.map((unit) => (
                  <MenuItem key={unit.id} value={unit.id}>
                    {unit.unitNumber || unit.name || `Unit ${unit.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Date & Time"
              type="datetime-local"
              value={formData.scheduledAt}
              onChange={(e) => setFormData({ ...formData, scheduledAt: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required
              fullWidth
            />

            <TextField
              label="Contact Name"
              value={formData.contactName}
              onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
              required
              fullWidth
            />

            <TextField
              label="Contact Email"
              type="email"
              value={formData.contactEmail}
              onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
              fullWidth
            />

            <TextField
              label="Contact Phone"
              type="tel"
              value={formData.contactPhone}
              onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
              fullWidth
            />

            <TextField
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />

            {editingShowing && (
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  label="Status"
                >
                  {editingShowing.status === 'pending' && (
                    <MenuItem value="pending">Pending (waiting for you to confirm)</MenuItem>
                  )}
                  <MenuItem value="scheduled">Scheduled</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="no-show">No Show</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={handleCloseDialog}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} /> : null}
          >
            {saving ? 'Saving…' : editingShowing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShowingScheduler;

